import type { ChessConstructor, ChessLike, ChessMove } from "./chessTypes.ts";

/** Number an illustrative line from either player's side. */
export function formatSampleLine(line: string[], start: number, side: "w" | "b"): string {
  return line
    .map((san, ply) => {
      const whiteMove = (side === "w") === (ply % 2 === 0);
      const number = start + Math.floor((ply + (side === "b" ? 1 : 0)) / 2);
      return whiteMove ? `${number}. ${san}` : ply === 0 ? `${number}… ${san}` : san;
    })
    .join(" ");
}

export function safeMove(
  chess: ChessLike,
  move: string | { from: string; to: string; promotion?: string }
): ChessMove | null {
  try {
    return chess.move(move);
  } catch {
    return null;
  }
}

const PIECE_WORDS: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};

/** A played move in words for players new to notation: "Pawn from c2 to c3". */
export function moveInWords(move: ChessMove): string {
  const check = move.san.endsWith("#")
    ? ", checkmate"
    : move.san.endsWith("+")
      ? ", giving check"
      : "";
  const rank = move.from[1];
  if (move.flags?.includes("k")) {
    return `Castle kingside: king from e${rank} to g${rank}, rook from h${rank} to f${rank}${check}`;
  }
  if (move.flags?.includes("q")) {
    return `Castle queenside: king from e${rank} to c${rank}, rook from a${rank} to d${rank}${check}`;
  }
  const piece = PIECE_WORDS[move.piece || "p"];
  const path = move.flags?.includes("e")
    ? `takes the pawn en passant, landing on ${move.to}`
    : move.captured
      ? `takes the ${PIECE_WORDS[move.captured]} on ${move.to}`
      : `to ${move.to}`;
  const promotion = move.promotion ? `, becoming a ${PIECE_WORDS[move.promotion]}` : "";
  return `${piece[0].toUpperCase()}${piece.slice(1)} from ${move.from} ${path}${promotion}${check}`;
}

export function sanToUci(Chess: ChessConstructor, fen: string, san: string): string | null {
  const chess = new Chess(fen);
  const move = safeMove(chess, san);
  if (!move) return null;
  return move.from + move.to + (move.promotion || "");
}

export function uciToSan(Chess: ChessConstructor, fen: string, uci: string): string | null {
  const chess = new Chess(fen);
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  const promotion = uci.length > 4 ? uci[4] : undefined;
  const move = safeMove(chess, { from, to, promotion });
  return move ? move.san : null;
}

export function playSans(Chess: ChessConstructor, sans: string[], startFen?: string) {
  const chess = startFen ? new Chess(startFen) : new Chess();
  const played: string[] = [];
  for (const san of sans) {
    const move = safeMove(chess, san);
    if (!move) {
      throw new Error(`Illegal SAN "${san}" after ${played.join(" ")}`);
    }
    played.push(move.san);
  }
  return chess;
}
